import React, { useState, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { ChartBarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const ErrorCategoryChart = ({ filters }) => {
  const [focus, setFocus] = useState(false);
  const chartRef = useRef(null);
  const { accessToken } = useAuth();

  const [labels, setLabels] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });

        const response = await api.get(
          `${baseUrl}/superAdminDashboard/getSuperAdmiErrorCategoryCount`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filteredParams,
          }
        );

        if (response.data.success && response.data.data) {
          const resData = response.data.data;
          setLabels(resData.map((item) => item._id));
          setRawData(resData.map((item) => item.count));
        }
      } catch (err) {
        console.error('Error fetching chart data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, accessToken]);

  const projectedData = rawData.length > 1
    ? [...rawData.slice(0, -1), null, rawData[rawData.length - 1] + 2]
    : [];

  const data = {
    labels,
    datasets: [
      {
        label: 'Error Count',
        data: rawData,
        fill: 'start',
        borderColor: '#EE1D23',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(238,29,35,0.25)');
          gradient.addColorStop(1, 'rgba(255,255,255,0)');
          return gradient;
        },
        pointBackgroundColor: '#1A2B5C',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#EE1D23',
        pointHoverBorderColor: '#64748b',
        pointRadius: (ctx) => ctx.dataIndex === rawData.length - 1 ? 9 : 6,
        pointHoverRadius: 12,
        borderWidth: 3,
        tension: 0.4,
      },
      {
        label: 'Projected',
        data: projectedData,
        borderDash: [6, 6],
        borderColor: '#64748b',
        borderWidth: 2,
        fill: false,
        pointRadius: 0,
        tension: 0.35,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1500,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1A2B5C',
        bodyColor: '#0f172a',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        padding: 14,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          label: (ctx) => {
            const current = ctx.raw;
            const prev = ctx.dataIndex > 0 ? ctx.dataset.data[ctx.dataIndex - 1] : null;
            const diff = prev ? (((current - prev) / prev) * 100).toFixed(1) : null;
            const change = diff ? ` (${diff > 0 ? '🔺' : '🔻'} ${Math.abs(diff)}%)` : '';
            return ` ${ctx.dataset.label}: ${current}${change}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#1A2B5C',
          font: { size: 13, weight: 'bold' },
          callback: (val) => {
            const label = labels[val];
            return label?.length > 12 ? label.slice(0, 12) + '…' : label;
          },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#64748b',
          font: { size: 12 },
        },
        grid: {
          color: '#E5E7EB',
          borderDash: [5],
        },
      },
    },
  };

  const noData = rawData.length === 0 || rawData.every((v) => v === 0 || v == null);

  const LoadingSkeleton = () => (
    <div className="flex flex-col items-center justify-center h-64 text-center animate-pulse gap-3">
      <div className="w-3/4 h-5 bg-gray-200 rounded" />
      <div className="w-1/2 h-4 bg-gray-200 rounded" />
      <div className="w-full h-52 bg-gray-100 rounded-xl" />
    </div>
  );

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <ChartBarIcon className="w-10 h-10 text-[#EE1D23]/80 mb-2" />
      <p className="text-lg font-semibold text-[#1A2B5C]">No Data Available</p>
      <span className="text-sm text-gray-500">Try adjusting filters or refresh again.</span>
    </div>
  );

  const ChartCard = () => (
    <div className="mt-10 bg-white/60 border border-[#f3f4f6] backdrop-blur-xl shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-3xl p-6 transition-all hover:shadow-2xl">
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-2">
          <ChartBarIcon className="w-6 h-6 text-[#EE1D23]" />
          <h2 className="text-xl font-bold bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] bg-clip-text text-transparent">
            Error Category Trend
          </h2>
        </div>
        <button
          onClick={() => setFocus(true)}
          className="px-4 py-1.5 border border-[#1A2B5C] text-sm rounded-md hover:bg-[#1A2B5C] hover:text-white transition"
        >
          Focus Mode
        </button>
      </div>

      <div className="h-[320px]">
        {loading ? <LoadingSkeleton /> : noData ? <NoDataMessage /> : (
          <Line ref={chartRef} data={data} options={options} />
        )}
      </div>
    </div>
  );

  const FocusMode = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4">
      <div className="bg-white border border-gray-300 rounded-3xl w-full max-w-6xl h-[90vh] p-8 overflow-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-6 h-6 text-[#1A2B5C]" />
            <h2 className="text-2xl font-bold text-[#1A2B5C]">Error Category Trend (Focus Mode)</h2>
          </div>
          <button
            onClick={() => setFocus(false)}
            className="bg-white shadow rounded-full p-2 text-gray-600 hover:text-[#EE1D23]"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="h-[70vh]">
          {loading ? <LoadingSkeleton /> : noData ? <NoDataMessage /> : (
            <Line ref={chartRef} data={data} options={options} />
          )}
        </div>
      </div>
    </div>
  );

  return <>{focus ? <FocusMode /> : <ChartCard />}</>;
};

export default ErrorCategoryChart;
